import React, { useState, useEffect } from 'react';
import { useTheme, ThemeProvider } from './ThemeContext';
import Start from './components/Start';
import Colors from './components/Colors';
import Typography, { markup as typographyMarkup } from './components/Typography';
import Container, { markup as containerMarkup } from './components/Container';
import Accordion, { markup as accordionMarkup } from './components/Accordion';
import Button, { markup as buttonMarkup } from './components/Button';
import Cards, { markup as cardMarkup } from './components/Cards';
import Hero, { markup as heroMarkup } from './components/Hero';
import Modal, { markup as modalMarkup } from './components/Modal';
import Text, { markup as textMarkup } from './components/Text';
import Form, { markup as formMarkup } from './components/Form';
import PreviewComponent from './PreviewComponent';
import { generateTheme, generateColorCss } from './utils';
import defaultValues from './theme.json';
import brand from './img/brand.svg';
import './scss/App.scss';

const App = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

const AppContent = () => {
  const [selectedComponent, setSelectedComponent] = useState('Start');
  const renderComponent = () => {
    switch (selectedComponent) {
      case 'Start':
        return <Start />;
      case 'Colors':
        return <Colors />;
      case 'Typography':
        return (
          <PreviewComponent
            Component={Typography}
            ComponentName="Typography"
            Id="TypographyKey"
            markup={typographyMarkup}
          />
        );
      case 'Container':
        return (
          <PreviewComponent
            Component={Container}
            ComponentName="Container"
            Id="ContainerKey"
            markup={containerMarkup}
          />
        );
      case 'Button':
        return (
          <PreviewComponent
            Component={Button}
            ComponentName="Button"
            Id="ButtonKey"
            markup={buttonMarkup}
          />
        );
      case 'Accordion':
        return (
          <PreviewComponent
            Component={Accordion}
            ComponentName="Accordion"
            Id="AccordionKey"
            markup={accordionMarkup}
          />
        );
      case 'Cards':
        return (
          <PreviewComponent
            Component={Cards}
            ComponentName="Cards"
            Id="CardsKey"
            markup={cardMarkup}
          />
        );
      case 'Hero':
        return (
          <PreviewComponent
            Component={Hero}
            ComponentName="Hero"
            Id="HeroKey"
            markup={heroMarkup}
          />
        );
      case 'Modal':
        return (
          <PreviewComponent
            Component={Modal}
            ComponentName="Modal"
            Id="ModalKey"
            markup={modalMarkup}
          />
        );
      case 'Form':
        return (
          <PreviewComponent
            Component={Form}
            ComponentName="Form"
            Id="FormKey"
            markup={formMarkup}
          />
        );
      case 'Text':
        return (
          <PreviewComponent
            Component={Text}
            ComponentName="Text"
            Id="TextKey"
            markup={textMarkup}
          />
        );
      default:
        return null;
    }
  };

  const { theme = {} } = useTheme();

  // generate initial color css to make sure that the components are styled correctly
  useEffect(() => {
    const initialComponents = [
      'Typography',
      'Container',
      'Button',
      'Accordion',
      'Cards',
      'Hero',
      'Form',
      'Modal',
      'Text',
    ];
    const storedColors =
      JSON.parse(localStorage.getItem('themeColors')) || defaultValues.theme.Colors;
    if (storedColors) {
      generateColorCss(storedColors);
    } else if (theme.colorProps) {
      generateColorCss(theme.colorProps);
    }
    initialComponents.forEach((component) => {
      if (JSON.parse(localStorage.getItem(component)) === null) {
        const componentDefaults = Object.entries(defaultValues.theme[component].values).reduce(
          (acc, [key, value]) => {
            acc[key] = value.default;
            return acc;
          },
          {},
        );
        localStorage.setItem(component, JSON.stringify(componentDefaults));
      }
    });
    if (JSON.parse(localStorage.getItem('themeColors') === null)) {
      localStorage.setItem('themeColors', JSON.stringify(defaultValues.theme.Colors));
    }
  }, [theme]);

  return (
    <div className="app">
      <div className="ui-main">
        <div className="start-mobile-modal">
          <div style={{ padding: '20px' }}>
            <svg
              width="80"
              height="80"
              viewBox="0 0 80.37 80.37"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect fill="#5940e6" height="80.37" rx="12" width="80.37" />
              <g fill="#fff">
                <path d="m24.43 59.93c-.55 0-.72-.35-.38-.79l14.25-18.17c.34-.43.34-1.14 0-1.57l-14.25-18.17c-.34-.43-.17-.79.38-.79h31.52c.55 0 .72.35.38.79l-14.25 18.17c-.34.43-.34 1.14 0 1.57l14.25 18.17c.34.43.17.79-.38.79zm15.12-22.23c.34.43.89.43 1.23 0l11.3-14.37c.34-.43.17-.79-.38-.79h-23.02c-.55 0-.72.35-.38.79l11.25 14.36zm12.15 20.13c.55 0 .72-.35.38-.79l-11.3-14.37c-.34-.43-.9-.43-1.23 0l-11.25 14.36c-.34.43-.17.79.38.79h23.02z" />
                <path d="m55.95 60.93h-31.52c-.66 0-1.17-.29-1.42-.79s-.16-1.09.25-1.61l14.25-18.18c.05-.07.05-.27 0-.33l-14.25-18.17c-.4-.51-.5-1.11-.26-1.61s.77-.8 1.42-.8h31.52c.66 0 1.17.29 1.42.79s.16 1.09-.25 1.61l-14.25 18.18c-.05.07-.05.27 0 .33l14.25 18.17c.4.51.5 1.11.26 1.61s-.77.8-1.42.8zm-30.46-2h29.41l-13.6-17.34c-.62-.79-.62-2.02 0-2.81l13.6-17.34h-29.41s13.6 17.34 13.6 17.34c.62.79.62 2.02 0 2.81zm26.21-.1h-23.02c-.65-.01-1.18-.31-1.42-.81s-.15-1.1.26-1.61l11.25-14.36c.34-.44.85-.7 1.39-.71.58 0 1.06.25 1.42.7l11.3 14.37c.41.53.5 1.12.25 1.62s-.76.79-1.42.79zm-21.96-2.01h20.9s-10.47-13.31-10.47-13.31zm10.61-13.54s0 0 0 0zm-.18-4.26c-.54 0-1.05-.26-1.4-.7l-.22-.29-11.04-14.09c-.41-.53-.5-1.12-.25-1.62s.76-.79 1.42-.79h23.02c.65 0 1.18.3 1.42.8s.15 1.1-.26 1.61l-11.3 14.37c-.35.45-.86.7-1.4.7zm-10.43-15.48 10.43 13.32 10.47-13.32h-20.91z" />
              </g>
            </svg>
          </div>
          This is the prototype of themex 🚀. Please use this application on a desktop computer.
        </div>
        <div className="ui-sidebar">
          <div className="ui-header">
            <div className="ui-brand">
              <img src={brand} alt="brand" />
            </div>
          </div>
          <div className="sidebar-navigation">
            <div className="sidebar-subtitle">Foundations</div>
            <button
              className={`ui-navigation-button ${selectedComponent === 'Colors' ? 'active' : ''}`}
              onClick={() => setSelectedComponent('Colors')}
            >
              Color
            </button>
            <button
              className={`ui-navigation-button ${
                selectedComponent === 'Typography' ? 'active' : ''
              }`}
              onClick={() => setSelectedComponent('Typography')}
            >
              Typography
            </button>
            <button
              className={`ui-navigation-button ${
                selectedComponent === 'Container' ? 'active' : ''
              }`}
              onClick={() => setSelectedComponent('Container')}
            >
              Container
            </button>
            <div className="sidebar-subtitle">Shared components</div>
            <button
              className={`ui-navigation-button ${selectedComponent === 'Button' ? 'active' : ''}`}
              onClick={() => setSelectedComponent('Button')}
            >
              Button
            </button>
            <div className="sidebar-subtitle">Components</div>
            <button
              className={`ui-navigation-button ${
                selectedComponent === 'Accordion' ? 'active' : ''
              }`}
              onClick={() => setSelectedComponent('Accordion')}
            >
              Accordion
            </button>
            <button
              className={`ui-navigation-button ${selectedComponent === 'Cards' ? 'active' : ''}`}
              onClick={() => setSelectedComponent('Cards')}
            >
              Card
            </button>
            <button
              className={`ui-navigation-button ${selectedComponent === 'Hero' ? 'active' : ''}`}
              onClick={() => setSelectedComponent('Hero')}
            >
              Hero
            </button>
            <button
              className={`ui-navigation-button ${selectedComponent === 'Text' ? 'active' : ''}`}
              onClick={() => setSelectedComponent('Text')}
            >
              Text section
            </button>
            <button
              className={`ui-navigation-button ${selectedComponent === 'Modal' ? 'active' : ''}`}
              onClick={() => setSelectedComponent('Modal')}
            >
              Modal
            </button>
            <button
              className={`ui-navigation-button ${selectedComponent === 'Form' ? 'active' : ''}`}
              onClick={() => setSelectedComponent('Form')}
            >
              Form
            </button>
          </div>
          <div className="sidebar-actions">
            <button onClick={() => generateTheme()} className="ui-action-button">
              Generate theme
            </button>
          </div>
        </div>
        <div className="ui-component">{renderComponent()}</div>
      </div>
    </div>
  );
};

export default App;
